import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import BaseModal from '../../../shared/components/base.modal';
import apiClient from '../../../shared/auth.interceptor';
import { errorToast } from '../../../shared/toast.service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { syncFirms } from '../../../shared/redux/syncFirms';
import { firmRefresh } from '../../../shared/auth.functions';
import EntityMultiSelectModal, { EntityOption } from './entity-multi-select.modal';
import { IUser } from '../../../user/user.interface';

// Props
interface AssignClientsToUserModalProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  user?: IUser;
}

const AssignClientsToUser: React.FC<AssignClientsToUserModalProps> = ({ open, handleClose, onSuccess, user }) => {
  const dispatch = useDispatch();
  const allCompanies = useSelector((state: RootState) => state.companyList.companyList);
  const currentFirm = useSelector((state: RootState) => state.currentFirm.currentFirm);

  // Компании текущей фирмы (или просто валидные компании)
  const companies = useMemo(() => {
    return (allCompanies || []).filter((c: any) => !!c); // оставляем как в исходном коде
  }, [allCompanies]);

  // Опции для мультиселекта: ключ — realmId, лейбл — companyName
  const options: EntityOption<any>[] = useMemo(
    () =>
      companies.map((c: any) => ({
        key: c.realmId,
        label: c.companyName,
        raw: c
      })),
    [companies]
  );

  // Выбранные компании по ключам (realmId)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);

  // При открытии модалки — предзаполняем выбранные компании (там, где sharedForUsers содержит нашего userId)
  useEffect(() => {
    if (!open) {
      setSelectedCompanyIds([]);
      return;
    }
    const userId = user?._id ?? '';
    const preselected = companies
      .filter((c: any) => Array.isArray(c.sharedForUsers) && c.sharedForUsers.includes(userId))
      .map((c: any) => c.realmId);
    setSelectedCompanyIds(preselected);
  }, [open, companies, user?._id]);

  const handleSubmit = async () => {
    if (!currentFirm?._id || !user?._id) {
      errorToast('Please select a firm and user');
      return;
    }
    try {
      await apiClient.post('/v1/firm/assign-clients/' + (currentFirm?._id ?? ''), {
        userId: user?._id,
        companyIds: selectedCompanyIds
      });

      dispatch(syncFirms(await firmRefresh()) as any);

      onSuccess();
      handleClose();
    } catch (error: any) {
      errorToast(error?.response?.data?.message ?? 'Error');
    }
  };

  const handleModalClose = () => {
    setSelectedCompanyIds([]);
    handleClose();
  };

  return (
    <BaseModal open={open} handleClose={handleModalClose} width={600} minHeight={400}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 350 }}>
        <EntityMultiSelectModal
          open={open}
          title="Clients"
          label="Clients"
          handleClose={handleModalClose}
          options={options}
          valueKeys={selectedCompanyIds}
          onChange={setSelectedCompanyIds}
          onSubmit={handleSubmit}
          submitLabel="Update"
          emptyText="No companies available"
        />
      </Box>
    </BaseModal>
  );
};

export default AssignClientsToUser;
